"use client";

import { useState } from "react";
import { KOREAN_REGIONS_DETAILED, formatRegion } from "@/lib/utils/regions";
import { updateUserProfile } from "@/lib/profile/api";
import toast from "react-hot-toast";

interface RegionSelectModalProps {
  isOpen: boolean;
  userId: string;
  onComplete: () => void;
}

export function RegionSelectModal({
  isOpen,
  userId,
  onComplete,
}: RegionSelectModalProps) {
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!selectedProvince) {
      toast.error("시/도를 선택해주세요.");
      return;
    }

    const selectedRegion = KOREAN_REGIONS_DETAILED.find(
      r => r.value === selectedProvince
    );

    // 도 단위인 경우 시 선택 필수
    if (selectedRegion?.cities && selectedRegion.cities.length > 0 && !selectedCity) {
      toast.error("시를 선택해주세요.");
      return;
    }

    setSaving(true);

    try {
      const regionValue = selectedCity
        ? formatRegion(selectedProvince, selectedCity)
        : selectedProvince;

      const result = await updateUserProfile(userId, {
        region: regionValue,
      });

      if (result.success) {
        toast.success("거래 지역이 설정되었습니다.");
        onComplete();
      } else {
        toast.error(result.error || "지역 설정에 실패했습니다.");
      }
    } catch (error) {
      console.error("지역 설정 실패:", error);
      toast.error("지역 설정 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          거래 지역 선택
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          거래 지역을 선택해주세요.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              시/도 <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedProvince}
              onChange={e => {
                setSelectedProvince(e.target.value);
                setSelectedCity("");
              }}
              className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A56E8]"
            >
              <option value="">시/도를 선택하세요</option>
              {KOREAN_REGIONS_DETAILED.map(region => (
                <option key={region.value} value={region.value}>
                  {region.label}
                </option>
              ))}
            </select>
          </div>

          {selectedProvince &&
            KOREAN_REGIONS_DETAILED.find(r => r.value === selectedProvince)
              ?.cities && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  시 <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCity}
                  onChange={e => setSelectedCity(e.target.value)}
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A56E8]"
                >
                  <option value="">시를 선택하세요</option>
                  {KOREAN_REGIONS_DETAILED.find(r => r.value === selectedProvince)
                    ?.cities?.map(city => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                </select>
              </div>
            )}

          <button
            onClick={handleSave}
            disabled={saving || !selectedProvince}
            className="w-full h-12 bg-[#7A56E8] hover:bg-[#6A46D8] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          >
            {saving ? "저장 중..." : "완료"}
          </button>
        </div>
      </div>
    </div>
  );
}

